import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1TutoraiUsageQueryKey,
  usePutApiV1TutoraiUsagePersonidLimit,
} from '@/shared/api/generated';

/** Valores editáveis dos limites de token de um aluno. */
export interface TokenLimitFormValues {
  readonly dailyLimit: number;
  readonly weeklyLimit: number;
  readonly monthlyLimit: number;
  readonly enabled: boolean;
}

/**
 * Callbacks de conclusão do `save`.
 *
 * A UI precisa saber o resultado do PUT para dar um toast honesto: o interceptor global do
 * Axios **não** emite toast de 4xx/5xx, então uma mutation que engole o erro fica muda.
 */
export interface UseTutorStudentLimitOptions {
  readonly onSaved?: () => void;
  readonly onError?: () => void;
}

/**
 * Atualização dos limites de token de um aluno.
 *
 * Invalida a query de uso sem os params de período: a resposta do PUT traz os limites, mas
 * não os consumos e flags derivados deles (`monthlyPercentUsed`, `*LimitReached`), que o
 * servidor recalcula.
 *
 * A chave gerada é `[{ url: '/api/v1/tutorai/usage' }, params?]` e o `personId` virou **query
 * param**, então ele viaja junto de `start`/`end` no segundo elemento. Passar só o `personId`
 * ainda é o filtro certo: o `invalidateQueries` casa chaves parcialmente — compara apenas as
 * chaves presentes no filtro —, então `{ personId }` casa com qualquer período **deste** aluno,
 * com nenhum outro aluno e com nenhuma consulta agregada (sem params).
 *
 * @param personId - Id da pessoa
 * @param options - Callbacks de sucesso e erro
 * @returns A ação de salvar e se o PUT está em andamento
 *
 * @example
 * ```tsx
 * const { save, isSaving } = useTutorStudentLimit(55, { onSaved: () => alert(...) });
 * ```
 */
export function useTutorStudentLimit(personId: number, options: UseTutorStudentLimitOptions = {}) {
  const queryClient = useQueryClient();

  const mutation = usePutApiV1TutoraiUsagePersonidLimit({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1TutoraiUsageQueryKey({ personId }),
        });
        options.onSaved?.();
      },
      onError: () => options.onError?.(),
    },
  });

  // Depende do `mutate`, não do objeto de mutation: o objeto é uma referência nova a cada
  // render, o que tornaria `save` instável e derrotaria o `useCallback`. O `mutate` do
  // TanStack Query é estável.
  const { mutate } = mutation;

  const save = useCallback(
    (values: TokenLimitFormValues) => mutate({ personId, data: values }),
    [mutate, personId],
  );

  return { save, isSaving: mutation.isPending } as const;
}
